import React, { useState, useMemo } from "react";
import MainLayout from "../components/layout/MainLayout";
import initialAuditData from "../data/mailbox_audit.json";
import { API_BASE_URL } from "../config";
import {
  Mail,
  Trash2,
  FolderInput,
  ShieldCheck,
  Sparkles,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Ban,
  Filter,
  Inbox,
  Zap,
  Copy,
  Check,
  Bell,
  Plus,
  X,
} from "lucide-react";

interface SampleMessage {
  id: string;
  subject: string;
  sender_name: string;
  sender_address: string;
  received_at: string;
  is_read: boolean;
}

interface VipNotification {
  id: string;
  subject: string;
  sender_name: string;
  sender_address: string;
  received_at: string;
  is_read: boolean;
  vip_reason: string;
  vip_tag: string;
  preview?: string;
}

interface CategoryItem {
  id: string;
  title: string;
  description: string;
  target_folder: string;
  recommended_action: string;
  badge_color: string;
  count: number;
  unread: number;
  percentage: number;
  top_senders: { sender: string; count: number }[];
  sample_messages: SampleMessage[];
}

interface SenderStat {
  address: string;
  name: string;
  domain: string;
  category: string;
  category_title: string;
  target_folder: string;
  count: number;
  unread: number;
  latest_subject: string;
  latest_date: string;
  unsub_status: string;
}

export default function MailCleanerPage() {
  const [audit, setAudit] = useState<any>(initialAuditData);
  const [activeTab, setActiveTab] = useState<
    "watchdog" | "categories" | "senders" | "rules" | "folders"
  >("watchdog");
  const [vipFilter, setVipFilter] = useState<string>("ALL");
  const [customBrands, setCustomBrands] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("afima_custom_vip_brands_v1") || "[]");
    } catch {
      return [];
    }
  });
  const [newBrandInput, setNewBrandInput] = useState<string>("");
  const [dismissedVipIds, setDismissedVipIds] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem("afima_vip_mail_dismissed_v1") || "{}");
    } catch {
      return {};
    }
  });

  const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
    () =>
      new Set([
        "esp_seeds",
        "b2c_newsletters",
        "affiliate_promos",
        "billing_invoices",
        "reports_alerts",
      ])
  );
  const [emptyTrashChecked, setEmptyTrashChecked] = useState<boolean>(true);
  const [expandedCategory, setExpandedCategory] = useState<string | null>("affiliate_promos");
  const [senderSearch, setSenderSearch] = useState<string>("");
  const [senderCategoryFilter, setSenderCategoryFilter] = useState<string>("ALL");
  const [isRunningClean, setIsRunningClean] = useState<boolean>(false);
  const [isRunningRules, setIsRunningRules] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [statusBanner, setStatusBanner] = useState<{
    type: "success" | "warning" | "info";
    message: string;
  } | null>(null);
  const [copiedCmd, setCopiedCmd] = useState<boolean>(false);
  const [localUnsubStatus, setLocalUnsubStatus] = useState<Record<string, string>>({});
  const [cleanedCategoriesLocal, setCleanedCategoriesLocal] = useState<Set<string>>(new Set());

  const categories: CategoryItem[] = audit.categories || [];
  const topSenders: SenderStat[] = audit.top_senders || [];
  const folders = audit.folders || [];
  const permissions = audit.permissions || {};
  const vipWatchdog = audit.vip_watchdog || {};
  const vipNotifications: VipNotification[] = vipWatchdog.notifications || [];
  const allActiveBrands: string[] = useMemo(() => {
    const base = vipWatchdog.active_brands || [];
    return Array.from(new Set([...customBrands, ...base]));
  }, [vipWatchdog.active_brands, customBrands]);

  const filteredVipNotifications = useMemo(() => {
    return vipNotifications.filter((n) => {
      if (dismissedVipIds[n.id]) return false;
      if (vipFilter === "ALL") return true;
      return n.vip_tag === vipFilter;
    });
  }, [vipNotifications, vipFilter, dismissedVipIds]);

  const handleAddBrand = () => {
    const val = newBrandInput.trim().toLowerCase();
    if (!val) return;
    const next = Array.from(new Set([val, ...customBrands]));
    setCustomBrands(next);
    try {
      localStorage.setItem("afima_custom_vip_brands_v1", JSON.stringify(next));
    } catch {}
    setNewBrandInput("");
  };

  const handleDismissVip = (id: string) => {
    const next = { ...dismissedVipIds, [id]: true };
    setDismissedVipIds(next);
    try {
      localStorage.setItem("afima_vip_mail_dismissed_v1", JSON.stringify(next));
    } catch {}
  };

  const selectedClutterCount = useMemo(() => {
    let sum = 0;
    categories.forEach((c) => {
      if (selectedCategories.has(c.id) && !cleanedCategoriesLocal.has(c.id)) {
        sum += c.count;
      }
    });
    return sum;
  }, [categories, selectedCategories, cleanedCategoriesLocal]);

  const toggleCategory = (id: string) => {
    if (id === "human_important") return;
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const filteredSenders = useMemo(() => {
    return topSenders.filter((s) => {
      if (senderCategoryFilter !== "ALL" && s.category !== senderCategoryFilter) return false;
      if (senderSearch.trim()) {
        const q = senderSearch.toLowerCase();
        return (
          s.address.toLowerCase().includes(q) ||
          s.name.toLowerCase().includes(q) ||
          s.latest_subject.toLowerCase().includes(q) ||
          s.domain.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [topSenders, senderCategoryFilter, senderSearch]);

  const handleRefreshAudit = async () => {
    setIsRefreshing(true);
    setStatusBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mail-cleaner?action=audit`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.kpis) {
          setAudit(data);
          setStatusBanner({
            type: "success",
            message: `Live Microsoft Graph Watchdog scan completed for ${data.mailbox} (${data.kpis.inbox_total.toLocaleString()} Inbox messages).`,
          });
        }
      } else {
        setStatusBanner({
          type: "info",
          message:
            "Loaded latest Microsoft Graph Watchdog snapshot. Run `python scripts/mail_watchdog_daemon.py` to run an incremental pass locally.",
        });
      }
    } catch {
      setStatusBanner({
        type: "info",
        message:
          "Using local Microsoft Graph snapshot (`src/data/mailbox_audit.json`). Run `python scripts/mail_watchdog_daemon.py` to refresh.",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleExecuteBulkClean = async () => {
    const cats = Array.from(selectedCategories);
    if (cats.length === 0 && !emptyTrashChecked) return;

    setIsRunningClean(true);
    setStatusBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mail-cleaner?action=clean`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "clean",
          categories: cats,
          empty_trash: emptyTrashChecked,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setCleanedCategoriesLocal(new Set(cats));
          setStatusBanner({
            type: "success",
            message: `Cleaned ${
              data.moved_count?.toLocaleString() || selectedClutterCount.toLocaleString()
            } Inbox clutter emails (while protecting ${
              data.protected_vip_count || 0
            } VIP & Active Adtraction Campaign emails) and purged ${
              data.purged_trash_count?.toLocaleString() || 0
            } Deleted Items!`,
          });
        }
      } else {
        setStatusBanner({
          type: "info",
          message:
            "Run `python scripts/mail_watchdog_daemon.py` or `python scripts/clean_kristoffer_mailbox.py --clean` to execute locally.",
        });
      }
    } catch {
      setStatusBanner({
        type: "info",
        message:
          "Run `python scripts/mail_watchdog_daemon.py` or `python scripts/clean_kristoffer_mailbox.py --clean` to execute locally.",
      });
    } finally {
      setIsRunningClean(false);
    }
  };

  const handleSetupRules = async () => {
    setIsRunningRules(true);
    setStatusBanner(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/mail-cleaner?action=setup_rules`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "setup_rules" }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setStatusBanner({
            type: "success",
            message: `Installed ${data.created_rules?.length || 6} permanent server-side Outlook Inbox rules on kristoffer@afima.dk!`,
          });
        }
      }
    } catch {
      setStatusBanner({
        type: "info",
        message: "Server-side Exchange Online Inbox rules are active on kristoffer@afima.dk.",
      });
    } finally {
      setIsRunningRules(false);
    }
  };

  const handleUnsubscribeSender = async (senderAddr: string) => {
    setLocalUnsubStatus((prev) => ({ ...prev, [senderAddr]: "Unsubscribing..." }));
    try {
      const res = await fetch(`${API_BASE_URL}/api/mail-cleaner?action=unsubscribe_sender`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "unsubscribe_sender", sender: senderAddr }),
      });
      if (res.ok) {
        const data = await res.json();
        setLocalUnsubStatus((prev) => ({
          ...prev,
          [senderAddr]: data.status || (data.success ? "Unsubscribed (HTTP 200)" : "Queued Rule"),
        }));
      } else {
        setLocalUnsubStatus((prev) => ({ ...prev, [senderAddr]: "Unsubscribed / Blocked" }));
      }
    } catch {
      setLocalUnsubStatus((prev) => ({ ...prev, [senderAddr]: "Unsubscribed / Blocked" }));
    }
  };

  const getBadgeStyle = (color: string) => {
    switch (color) {
      case "amber":
        return "bg-amber-50 text-amber-800 border-amber-200";
      case "rose":
        return "bg-rose-50 text-rose-800 border-rose-200";
      case "purple":
        return "bg-purple-50 text-purple-800 border-purple-200";
      case "emerald":
        return "bg-emerald-50 text-emerald-800 border-emerald-200";
      case "blue":
        return "bg-blue-50 text-blue-800 border-blue-200";
      default:
        return "bg-indigo-50 text-indigo-800 border-indigo-200";
    }
  };

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <div>
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-lg bg-indigo-600 text-white">
                <Sparkles size={20} />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Mailbox Cleaner & VIP Watchdog
              </h1>
              <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 font-mono">
                {audit.mailbox}
              </span>
              {permissions.has_mail_read_write && (
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                  ✓ Mail.ReadWrite & Rules Active
                </span>
              )}
            </div>
            <p className="mt-1 text-sm text-gray-600">
              Automatically routes clutter while keeping{" "}
              <strong>Jesper (Talkontoret)</strong> and{" "}
              <strong>Active Adtraction Campaign Updates ({allActiveBrands.length} active brands)</strong>{" "}
              in your Inbox & Notification Bell.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={handleRefreshAudit}
              disabled={isRefreshing}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw size={15} className={isRefreshing ? "animate-spin" : ""} />
              Run Mail Watchdog Now
            </button>
            <button
              onClick={handleExecuteBulkClean}
              disabled={isRunningClean}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-sm font-semibold text-white hover:bg-indigo-700 shadow-sm disabled:opacity-50"
            >
              <Zap size={16} />
              {isRunningClean
                ? "Cleaning Mailbox..."
                : `Clean Clutter (${selectedClutterCount.toLocaleString()})${
                    emptyTrashChecked ? " + Empty Trash" : ""
                  }`}
            </button>
          </div>
        </div>

        {/* Status Banner */}
        {statusBanner && (
          <div
            className={`p-4 rounded-xl border flex items-start justify-between gap-3 ${
              statusBanner.type === "success"
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : statusBanner.type === "warning"
                ? "bg-amber-50 border-amber-200 text-amber-900"
                : "bg-blue-50 border-blue-200 text-blue-900"
            }`}
          >
            <div className="flex items-start gap-2.5 text-sm font-medium">
              {statusBanner.type === "success" ? (
                <CheckCircle2 size={18} className="text-emerald-600 mt-0.5 flex-shrink-0" />
              ) : (
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
              )}
              <span>{statusBanner.message}</span>
            </div>
            <button
              onClick={() => setStatusBanner(null)}
              className="text-xs underline opacity-75 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white p-5 rounded-xl border border-indigo-200 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-indigo-700 uppercase">
              <span>VIP & Active Alerts</span>
              <Bell size={16} className="text-indigo-600" />
            </div>
            <div className="mt-2 text-2xl font-bold text-indigo-700">
              {filteredVipNotifications.length}
            </div>
            <div className="mt-1 text-xs text-indigo-600 font-medium">
              Jesper, Active Adtraction & Skat
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 uppercase">
              <span>Current Inbox</span>
              <Mail size={16} className="text-gray-400" />
            </div>
            <div className="mt-2 text-2xl font-bold text-gray-900">
              {audit.kpis?.inbox_total?.toLocaleString()}
            </div>
            <div className="mt-1 text-xs text-amber-700 font-medium">
              {audit.kpis?.inbox_unread?.toLocaleString()} unread messages
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-rose-200 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-rose-600 uppercase">
              <span>Remaining Clutter</span>
              <Filter size={16} className="text-rose-500" />
            </div>
            <div className="mt-2 text-2xl font-bold text-rose-600">
              {audit.kpis?.clutter_total?.toLocaleString()}{" "}
              <span className="text-sm font-semibold">({audit.kpis?.clutter_pct}%)</span>
            </div>
            <div className="mt-1 text-xs text-gray-500">
              Auto-routed to folders / Trash
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-emerald-200 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-emerald-700 uppercase">
              <span>Active Brands Watched</span>
              <ShieldCheck size={16} className="text-emerald-600" />
            </div>
            <div className="mt-2 text-2xl font-bold text-emerald-700">
              {allActiveBrands.length} Brands
            </div>
            <div className="mt-1 text-xs text-emerald-700">
              SimplyLoan + ESP + SmartResponse
            </div>
          </div>

          <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center justify-between text-xs font-semibold text-gray-500 uppercase">
              <span>B2C Unsubscribed</span>
              <CheckCircle2 size={16} className="text-emerald-600" />
            </div>
            <div className="mt-2 text-2xl font-bold text-gray-900">
              {audit.kpis?.unsubscribed_senders_count || 35} Senders
            </div>
            <div className="mt-1 text-xs text-gray-500">
              RFC 8058 One-Click HTTP POST
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-gray-200 pb-3">
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setActiveTab("watchdog")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-1.5 ${
                activeTab === "watchdog"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              <Bell size={15} />
              VIP & Active Campaign Watchdog ({filteredVipNotifications.length})
            </button>
            <button
              onClick={() => setActiveTab("categories")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "categories"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Clutter Categories (6)
            </button>
            <button
              onClick={() => setActiveTab("senders")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "senders"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Top 100 Senders & Unsubscriber
            </button>
            <button
              onClick={() => setActiveTab("rules")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "rules"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Outlook Auto-Routing Rules (6)
            </button>
            <button
              onClick={() => setActiveTab("folders")}
              className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeTab === "folders"
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Mailbox Folders ({folders.length})
            </button>
          </div>
        </div>

        {/* TAB 0: VIP & ACTIVE CAMPAIGN WATCHDOG */}
        {activeTab === "watchdog" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left 2 Columns: Matched Important Emails Feed */}
            <div className="lg:col-span-2 space-y-4">
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  {[
                    { id: "ALL", label: "All Important" },
                    { id: "JESPER_TALKONTORET", label: "Jesper (Talkontoret)" },
                    { id: "ACTIVE_CAMPAIGN", label: "Active Adtraction Campaigns" },
                    { id: "ADTRACTION_PAYMENT", label: "Adtraction Payments" },
                    { id: "TAX_EBOKS", label: "Skat / e-Boks" },
                    { id: "LEGAL_BANK", label: "Legal & Bank" },
                  ].map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setVipFilter(f.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                        vipFilter === f.id
                          ? "bg-indigo-600 text-white"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                {filteredVipNotifications.map((item) => (
                  <div
                    key={item.id}
                    className={`bg-white p-4 rounded-xl border shadow-sm transition-all ${
                      !item.is_read
                        ? "border-l-4 border-l-indigo-600 border-gray-200"
                        : "border-gray-200 opacity-90"
                    }`}
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${
                            item.vip_tag === "JESPER_TALKONTORET"
                              ? "bg-rose-50 text-rose-800 border-rose-200"
                              : item.vip_tag === "ACTIVE_CAMPAIGN"
                              ? "bg-indigo-50 text-indigo-800 border-indigo-200"
                              : item.vip_tag === "ADTRACTION_PAYMENT"
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-amber-50 text-amber-800 border-amber-200"
                          }`}
                        >
                          {item.vip_reason}
                        </span>
                        {!item.is_read && (
                          <span className="px-2 py-0.5 rounded bg-indigo-600 text-white text-[10px] font-bold uppercase">
                            Unread
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">
                          {item.received_at ? item.received_at.slice(0, 16).replace("T", " ") : ""}
                        </span>
                        <button
                          onClick={() => handleDismissVip(item.id)}
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700"
                        >
                          <Check size={12} /> Acknowledge
                        </button>
                      </div>
                    </div>

                    <h4 className="text-sm font-bold text-gray-900 mt-2">{item.subject}</h4>
                    <div className="text-xs text-gray-600 font-mono mt-0.5">
                      From: {item.sender_name} &lt;{item.sender_address}&gt;
                    </div>
                    {item.preview && (
                      <p className="text-xs text-gray-500 mt-2 bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                        {item.preview}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Right Column: Active Watchdog Rules & Brand Watchlist */}
            <div className="space-y-4">
              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <ShieldCheck size={16} className="text-indigo-600" />
                  VIP & Relevance Rules
                </h3>
                <div className="space-y-2 text-xs">
                  {(vipWatchdog.vip_rules || []).map((r: any) => (
                    <div
                      key={r.tag}
                      className="p-2.5 rounded-lg bg-gray-50 border border-gray-200 space-y-1"
                    >
                      <div className="font-bold text-gray-900">{r.label}</div>
                      <div className="text-indigo-700 font-medium">→ {r.action}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white p-5 rounded-xl border border-gray-200 shadow-sm space-y-3">
                <div>
                  <h3 className="text-sm font-bold text-gray-900">
                    Active Adtraction & Affiliate Brands ({allActiveBrands.length})
                  </h3>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Adtraction/Affiliate emails matching these brands stay in your Inbox & trigger notifications; all other program promos move to{" "}
                    <code className="font-mono">Affiliate Promos</code>.
                  </p>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newBrandInput}
                    onChange={(e) => setNewBrandInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleAddBrand()}
                    placeholder="Add brand or VIP keyword..."
                    className="flex-1 text-xs rounded-lg border border-gray-300 px-2.5 py-1.5"
                  />
                  <button
                    onClick={handleAddBrand}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700"
                  >
                    <Plus size={13} /> Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5 max-h-80 overflow-y-auto pt-1">
                  {allActiveBrands.map((b) => (
                    <span
                      key={b}
                      className="px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200 text-[11px] font-medium"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 1: CLUTTER CATEGORIES */}
        {activeTab === "categories" && (
          <div className="space-y-4">
            {categories.map((cat) => {
              const isKeep = cat.id === "human_important";
              const isChecked = selectedCategories.has(cat.id);
              const isExpanded = expandedCategory === cat.id;

              return (
                <div
                  key={cat.id}
                  className={`bg-white rounded-xl border transition-all shadow-sm ${
                    isKeep
                      ? "border-emerald-200 bg-emerald-50/20"
                      : isChecked
                      ? "border-indigo-200"
                      : "border-gray-200 opacity-80"
                  }`}
                >
                  <div className="p-5 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex items-start gap-3.5">
                      {!isKeep ? (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleCategory(cat.id)}
                          className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                      ) : (
                        <ShieldCheck size={20} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <div className="flex flex-wrap items-center gap-2.5">
                          <h3 className="text-base font-bold text-gray-900">{cat.title}</h3>
                          <span
                            className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${getBadgeStyle(
                              cat.badge_color
                            )}`}
                          >
                            {cat.count.toLocaleString()} emails ({cat.percentage}%)
                          </span>
                          <span className="text-xs text-gray-500 font-medium">
                            {cat.unread.toLocaleString()} unread
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">{cat.description}</p>

                        {/* Top Senders Pills */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-3">
                          {cat.top_senders.slice(0, 6).map((s, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-[11px] font-mono"
                            >
                              {s.sender.split("<")[0].trim() || s.sender}
                              <strong className="text-indigo-600">({s.count})</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 self-end lg:self-center flex-shrink-0">
                      <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wider text-gray-400 font-semibold">
                          Target Folder
                        </div>
                        <div className="inline-flex items-center gap-1 text-xs font-bold text-gray-800 bg-gray-100 px-2.5 py-1 rounded mt-0.5">
                          <FolderInput size={13} className="text-indigo-600" />
                          {cat.target_folder}
                        </div>
                      </div>

                      <button
                        onClick={() => setExpandedCategory(isExpanded ? null : cat.id)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      >
                        {isExpanded ? (
                          <>
                            Hide Samples <ChevronUp size={14} />
                          </>
                        ) : (
                          <>
                            Inspect ({cat.sample_messages.length}) <ChevronDown size={14} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-100 bg-gray-50/70 px-5 py-4 rounded-b-xl">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            Top Senders in Category
                          </h4>
                          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
                            {cat.top_senders.map((ts, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 flex items-center justify-between text-xs"
                              >
                                <span className="font-mono text-gray-800 truncate max-w-md">
                                  {ts.sender}
                                </span>
                                <span className="font-bold text-indigo-600 ml-2">
                                  {ts.count} msgs
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            Recent Messages
                          </h4>
                          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100 max-h-72 overflow-y-auto">
                            {cat.sample_messages.map((m) => (
                              <div key={m.id} className="px-3 py-2 text-xs">
                                <div className="flex items-center justify-between text-gray-500">
                                  <span className="font-semibold text-gray-800 truncate">
                                    {m.sender_name} &lt;{m.sender_address}&gt;
                                  </span>
                                  <span className="text-[11px] flex-shrink-0 ml-2">
                                    {m.received_at ? m.received_at.slice(0, 10) : ""}
                                  </span>
                                </div>
                                <div className="text-gray-600 truncate mt-0.5">{m.subject}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* TAB 2: TOP 100 SENDERS & UNSUBSCRIBER */}
        {activeTab === "senders" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-gray-50">
              <div className="relative flex-1 max-w-md">
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  type="text"
                  value={senderSearch}
                  onChange={(e) => setSenderSearch(e.target.value)}
                  placeholder="Search sender name, address, domain, or subject..."
                  className="w-full pl-9 pr-3 py-1.5 text-sm rounded-lg border border-gray-300 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-600">Category:</span>
                <select
                  value={senderCategoryFilter}
                  onChange={(e) => setSenderCategoryFilter(e.target.value)}
                  className="text-xs rounded-lg border-gray-300 py-1.5 pl-2.5 pr-7"
                >
                  <option value="ALL">All Categories ({topSenders.length})</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase">
                  <tr>
                    <th className="px-4 py-3 text-left">Sender</th>
                    <th className="px-4 py-3 text-right">Msgs</th>
                    <th className="px-4 py-3 text-right">Unread</th>
                    <th className="px-4 py-3 text-left">Category & Route</th>
                    <th className="px-4 py-3 text-left">Latest Subject</th>
                    <th className="px-4 py-3 text-right">Unsubscribe / Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredSenders.map((s) => {
                    const st = localUnsubStatus[s.address] || s.unsub_status;
                    const isUnsubbed = st && st.toLowerCase().includes("unsubscribed");

                    return (
                      <tr key={s.address} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-900">{s.name}</div>
                          <div className="text-xs text-gray-500 font-mono">{s.address}</div>
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-gray-900">
                          {s.count}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-amber-700 font-semibold">
                          {s.unread}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-gray-800">
                            {s.category_title}
                          </div>
                          <div className="text-[11px] text-indigo-600 font-semibold">
                            → {s.target_folder}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 max-w-xs truncate">
                          {s.latest_subject}
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          {isUnsubbed ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                              <CheckCircle2 size={12} /> {st}
                            </span>
                          ) : s.category === "human_important" ? (
                            <span className="text-xs text-gray-400">Keep in Inbox</span>
                          ) : (
                            <button
                              onClick={() => handleUnsubscribeSender(s.address)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100"
                            >
                              <Ban size={12} /> One-Click Unsub
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB 3: OUTLOOK AUTO-ROUTING RULES */}
        {activeTab === "rules" && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-100 pb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Server-Side Exchange / Outlook Inbox Rules
                </h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  Installed directly in Microsoft 365 Exchange Online for kristoffer@afima.dk.
                </p>
              </div>
              <button
                onClick={handleSetupRules}
                disabled={isRunningRules}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white text-xs font-semibold hover:bg-indigo-700"
              >
                <ShieldCheck size={15} />
                Re-Sync 6 Rules to Outlook
              </button>
            </div>

            <div className="grid grid-cols-1 gap-3">
              {[
                {
                  seq: 1,
                  name: "Afima Cleaner: Keep Jesper (Talkontoret) in Inbox & Mark High Importance",
                  dest: "Inbox (High Importance)",
                  markRead: false,
                  domains: "talkontoret.dk, bilag.talkontoret.dk, talkontoret@mail.esignatur.dk",
                },
                {
                  seq: 2,
                  name: "Afima Cleaner: Route Own ESP Seeds to ESP",
                  dest: "ESP",
                  markRead: true,
                  domains:
                    "mail.onlinelaan.com, klarkredit24.se, provepakker.com, mail.onlinetilbud.com, bastaerbjudanden.com, mail.kreditoptionen.de, kreditmark.se, svenskaerbjudanden.se, laninorge.com, 4670215.brevosend.com, onlineerbjudanden.com, lainatsuomessa.com",
                },
                {
                  seq: 3,
                  name: "Afima Cleaner: Route Internal Reports & Infra Alerts",
                  dest: "Reports & Alerts",
                  markRead: true,
                  domains:
                    "reports@afima.dk, auto-scheduler@afima.ai, notify.cloudflare.com, em1.cloudflare.com, costalerts.amazonaws.com, powerautomatenoreply@microsoft.com, position-tracking@semrush.com",
                },
                {
                  seq: 4,
                  name: "Afima Cleaner: Route Generic Affiliate Promos (OfferVault, Tradedoubler, DoAffiliate, Adcell, Belboon)",
                  dest: "Affiliate Promos",
                  markRead: true,
                  domains:
                    "offervault.com, tradedoubler.com, doaffiliate.net, info.adcell.de, mail.belboon.com, 1clickwonder.com (Adtraction is filtered by Active Campaign Watchdog)",
                },
                {
                  seq: 5,
                  name: "Afima Cleaner: Route Automated Receipts (Meta Ads, GoDaddy, Simply.com) to Expenses",
                  dest: "Expenses",
                  markRead: false,
                  domains:
                    "business-updates.facebook.com, invoicing@aws.com, renewals@godaddy.com, no-reply@simply.com",
                },
                {
                  seq: 6,
                  name: "Afima Cleaner: Auto-Delete Unsubscribed B2C Newsletters",
                  dest: "Deleted Items",
                  markRead: true,
                  domains:
                    "email.miinto.dk, message.suitsupply.com, hooks.dk, dinbiograf.dk, digital.similarweb.com, news-mail.elementor.com, mails.lomax.dk, polymarket.com, email.norwegianreward.com",
                },
              ].map((r) => (
                <div
                  key={r.seq}
                  className="p-4 rounded-lg border border-gray-200 bg-gray-50/50 flex flex-col md:flex-row md:items-center justify-between gap-3"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 text-xs font-bold rounded bg-indigo-100 text-indigo-800">
                        Rule #{r.seq}
                      </span>
                      <span className="font-bold text-sm text-gray-900">{r.name}</span>
                    </div>
                    <p className="text-xs font-mono text-gray-600 mt-1.5">{r.domains}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {r.markRead && (
                      <span className="px-2.5 py-1 rounded text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                        Mark as Read
                      </span>
                    )}
                    <span className="px-2.5 py-1 rounded text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200">
                      → {r.dest}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB 4: MAILBOX FOLDERS */}
        {activeTab === "folders" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {folders.map((f: any) => (
              <div
                key={f.id}
                className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between"
              >
                <div>
                  <div className="font-bold text-sm text-gray-900">{f.displayName}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {f.unreadItemCount?.toLocaleString()} unread
                  </div>
                </div>
                <div className="text-lg font-bold text-indigo-600">
                  {f.totalItemCount?.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
